import * as React from 'react';
import { EventHandler, SyntheticEvent, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import MediaQuery, { useMediaQuery } from 'react-responsive';

import ContactsForm from '../../../../../Contacts/components/Contacts';
import { CheckoutState } from '../../../../store/reducers';
import { InputType } from '../../../../../Passenger/types';
import { getOrderId, getIsReadonly, getIsRedirectFromMetasearch } from '../../../../store/order/selectors';
import { MOBILE_MIN_WIDTH } from '../../../../../utils';
import { reachGoal, SplitTestGoal } from '../../../../../analytics';
import MobileAuthForm from '../../../../../MobileAuthForm';
import { get, set, TEST_AUTHORIZATION_REFUSED } from '../../../../../cache';
import { fillOrder } from '../../../../store/order/actions';
import { CheckoutOrderFragment, GetAccountInfoQuery, LoginType, TravellerFieldEnum } from '@websky/graphql';
import { useRenderers } from '../../../../../renderProps';

interface OwnProps {
	submitForm: EventHandler<SyntheticEvent>;
	customer: CheckoutOrderFragment['customer'];
	isInputsReadonly?: boolean;
	userInfo?: GetAccountInfoQuery['CurrentUser'];
	refetchUser?: () => Promise<GetAccountInfoQuery>;
	onLoginOpen?: (preselectedLogin: string) => void;
	autofocus?: boolean;
}

interface StateProps {
	orderId: string;
	isReadonly: boolean;
	isRedirectFromMetasearch: boolean;
}

interface DispatchProps {
	fillOrder: typeof fillOrder;
}

type Props = OwnProps & StateProps & DispatchProps;

// #fixme: a lot of state objects
const Contacts: React.FunctionComponent<Props> = props => {
	const loginType = LoginType.Email;
	const [customerFormIsOpen, setCustomerFormIsOpen] = useState(false);
	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });

	const getUserInfo = async () => {
		const data = await props.refetchUser?.();

		if (!data?.CurrentUser) {
			// Show customer auth form if user is not authorized yet.
			const showCustomerForm =
				isMobile && !get(`${TEST_AUTHORIZATION_REFUSED}_${props.orderId}`) && !props.isReadonly;

			if (showCustomerForm) {
				// Somehow it doesn't work without timeout. The form gets re-rendered.
				setTimeout(() => setCustomerFormIsOpen(true), 200);
			}
		}
	};

	const closeCustomerFormWithRefused = () => {
		setCustomerFormIsOpen(false);
		set(`${TEST_AUTHORIZATION_REFUSED}_${props.orderId}`, true);
		reachGoal(SplitTestGoal.RefusedToAuthorize);
	};

	const closeCustomerFormWithAgreed = () => {
		setCustomerFormIsOpen(false);
		reachGoal(SplitTestGoal.AgreedToAuthorize);

		props.refetchUser?.();
	};

	const agreeToAuthorize = () => {
		reachGoal(SplitTestGoal.AgreedToAuthorize);
	};

	useEffect(() => {
		if (!props.isReadonly) {
			getUserInfo();
		}
	}, [props.isReadonly]);

	const email = props.customer.values.find(({ type }) => type === TravellerFieldEnum.Email);
	const phone = props.customer.values.find(({ type }) => type === TravellerFieldEnum.Phone);

	const open = !props.isReadonly || props.isInputsReadonly;

	let ContactsWidget: React.ComponentType<React.ComponentProps<typeof ContactsForm>> = useRenderers()
		.CheckoutContacts;

	if (!ContactsWidget) {
		ContactsWidget = ContactsForm;
	}

	return (
		<>
			<ContactsWidget
				open={open}
				isInputsReadonly={props.isInputsReadonly}
				autofocus={props.autofocus}
				isAuthorized={!!props.userInfo}
				toggleOpen={() => {}}
				submitForm={props.submitForm}
				onLoginOpen={props.onLoginOpen}
				loginType={loginType}
				showSubscribeCheckbox={props.customer.showSubscribeCheckbox}
				email={{
					name: `customer.${email.name}`,
					type: email.type,
					input: InputType.Text,
					defaultValue: email.value,
					validations: email.validationRules
				}}
				phone={{
					name: `customer.${phone.name}`,
					type: phone.type,
					input: InputType.Phone,
					defaultValue: phone.value,
					validations: phone.validationRules
				}}
			/>

			{!props.userInfo && !props.isRedirectFromMetasearch && (
				<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
					<MobileAuthForm
						isOpen={customerFormIsOpen}
						onClose={closeCustomerFormWithRefused}
						onSuccess={closeCustomerFormWithAgreed}
						onReject={closeCustomerFormWithRefused}
						onSocialAuthClick={agreeToAuthorize}
					/>
				</MediaQuery>
			)}
		</>
	);
};

const mapStateToProps = (state: CheckoutState): StateProps => {
	return {
		orderId: getOrderId(state),
		isReadonly: getIsReadonly(state),
		isRedirectFromMetasearch: getIsRedirectFromMetasearch(state)
	};
};

const mapDispatchToProps: DispatchProps = {
	fillOrder
};

export default connect(mapStateToProps, mapDispatchToProps)(Contacts);
